import { get, writable } from 'svelte/store';
import { Liquid } from 'liquidjs';

import { BizCardsService } from '@client/services/bizcards';

import { userStore } from '@client/stores/user';

/**
 * @typedef {import('svelte/store').Writable<T>} Writable
 * @template T
 */

const engine = new Liquid();

/**
 * Creates an isolated bizcard preview store instance and immediately loads the bizcard.
 * Each call returns its own set of stores and actions,
 * so multiple previews can coexist on the same page.
 *
 * @param {string} bizcardId - The rulename of the bizcard to load.
 */
export const createBizcardPreviewStore = (bizcardId) => {
  const id = writable('');
  const template = writable('');
  const renderedTemplate = writable('');
  const loading = writable(false);
  const selectedUser = writable(null);

  const reset = () => {
    id.set('');
    template.set('');
    renderedTemplate.set('');
    selectedUser.set(null);
  };

  /**
   * Loads a bizcard by ID and populates the template store.
   * @param {string} bizcardId - The rulename of the bizcard to load.
   */
  const loadBizcard = async (bizcardId) => {
    loading.set(true);
    reset();

    try {
      const response = await BizCardsService.getById(bizcardId);
      id.set(response.rulename);
      template.set(response.htmltemplate);
      await updatePreview();
    } catch (e) {
      console.error(e);
    } finally {
      loading.set(false);
    }
  };

  /**
   * Sets the selected user for preview rendering.
   * @param {any} user
   */
  const setSelectedUser = (user) => {
    selectedUser.set(user);
  };

  /**
   * Renders the preview by running the template through the Liquid engine
   * with the selected user's data.
   */
  const updatePreview = async () => {
    const tpl = get(template);

    if (!tpl) {
      renderedTemplate.set('');
      return;
    }

    const userConfig = get(userStore);
    const [sgcnameproto, sgcname] = userConfig.cname;
    const previewUser = get(selectedUser) || userConfig.currentUser;

    const previewData = {
      ...previewUser,
      sgcnameproto,
      sgcname,
      sgsusername: previewUser?.username
    };

    try {
      const parsedTemplate = engine.parse(tpl);
      const output = await engine.render(parsedTemplate, previewData);
      renderedTemplate.set(output);
    } catch (e) {
      console.error(e);
      renderedTemplate.set('');
    }
  };

  loadBizcard(bizcardId);

  return {
    id,
    template,
    renderedTemplate,
    loading,
    selectedUser,
    reset,
    loadBizcard,
    setSelectedUser,
    updatePreview
  };
};
